/**
 * SIMPLIFIED ROUTE CONFIGURATION
 *
 * This is the ONLY frontend routing configuration needed.
 * No duplication with backend menu - just maps paths to components.
 *
 * Backend menu.service.ts controls:
 * - What routes appear in navigation
 * - Permission filtering
 * - Menu structure
 * - Icons and labels
 *
 * Frontend (this file) only needs:
 * - Path → Component mapping
 */

import { lazy } from 'react';

// ============================================================================
// COMPONENT IMPORTS
// ============================================================================

// Public Pages
export const LoginPage = lazy(() => import('../pages/LoginPage'));
export const AcceptInvitationPage = lazy(() => import('../pages/AcceptInvitationPage'));
export const NotFoundPage = lazy(() => import('../pages/NotFoundPage'));
export const ErrorPage = lazy(() => import('../pages/ErrorPage'));

// Onboarding
export const WelcomePage = lazy(() => import('../pages/onboarding/WelcomePage'));
export const OrganizationOnboardingPage = lazy(
  () => import('../pages/onboarding/OrganizationOnboardingPage'),
);

// Dashboard
export const Dashboard = lazy(() => import('../pages/erp/DashboardPage'));
export const WaitingOnMePage = lazy(() => import('../pages/WaitingOnMePage'));

// Master Data
export const ItemPage = lazy(() => import('../pages/erp/master/ItemPage'));
export const ItemDetailPage = lazy(() => import('../pages/erp/master/ItemDetailPage'));
export const ItemGroupPage = lazy(() => import('../pages/erp/master/ItemGroupPage'));
export const WarehousePage = lazy(() => import('../pages/erp/master/WarehousePage'));
export const UomPage = lazy(() => import('../pages/erp/master/UomPage'));
export const UomCategoryPage = lazy(() => import('../pages/erp/master/UomCategoryPage'));
export const UomConversionPage = lazy(() => import('../pages/erp/master/UomConversionPage'));
export const BrandsPage = lazy(() => import('../pages/erp/master/BrandsPage'));
export const TransporterPage = lazy(() => import('../pages/erp/master/TransporterPage'));
export const PaymentTermsPage = lazy(() => import('../pages/erp/master/PaymentTermsPage'));
export const PartyPage = lazy(() => import('../pages/erp/party/PartyPage'));
export const BOMPage = lazy(() => import('../pages/erp/bom/BOMPage'));

// Products & Inventory
export const ProductsPage = lazy(() => import('../pages/erp/products/ProductsPage'));
export const OrdersPage = lazy(() => import('../pages/erp/orders/OrdersPage'));
export const InventoryPage = lazy(() => import('../pages/erp/inventory/InventoryPage'));
export const StockBalancesPage = lazy(() => import('../pages/erp/inventory/StockBalancesPage'));
export const StockLedgerPage = lazy(() => import('../pages/erp/inventory/StockLedgerPage'));
export const StockTransferPage = lazy(() => import('../pages/erp/inventory/StockTransferPage'));
export const StockTransferViewPage = lazy(
  () => import('../pages/erp/inventory/StockTransferViewPage'),
);
export const StockConsumptionPage = lazy(
  () => import('../pages/erp/inventory/StockConsumptionPage'),
);
export const InventoryReportsPage = lazy(
  () => import('../pages/erp/inventory/InventoryReportsPage'),
);

// Procurement
export const ProcurementDashboard = lazy(() => import('../pages/procurement/ProcurementDashboard'));
export const PurchaseOrderListPage = lazy(
  () => import('../pages/procurement/PurchaseOrderListPage'),
);
export const PurchaseOrderFormPage = lazy(
  () => import('../pages/procurement/PurchaseOrderFormPage'),
);
export const PurchaseOrderViewPage = lazy(
  () => import('../pages/procurement/PurchaseOrderViewPage'),
);
export const PurchaseReceiptListPage = lazy(
  () => import('../pages/procurement/PurchaseReceiptListPage'),
);
export const PurchaseReceiptFormPage = lazy(
  () => import('../pages/procurement/PurchaseReceiptFormPage'),
);
export const PurchaseReceiptViewPage = lazy(
  () => import('../pages/procurement/PurchaseReceiptViewPage'),
);
export const ProcurementVouchersListPage = lazy(
  () => import('../pages/procurement/ProcurementVouchersListPage'),
);
export const CreditNoteListPage = lazy(() => import('../pages/procurement/CreditNoteListPage'));
export const CreditNoteFormPage = lazy(() => import('../pages/procurement/CreditNoteFormPage'));
export const CreditNoteViewPage = lazy(() => import('../pages/procurement/CreditNoteViewPage'));
export const DebitNoteListPage = lazy(() => import('../pages/procurement/DebitNoteListPage'));
export const DebitNoteFormPage = lazy(() => import('../pages/procurement/DebitNoteFormPage'));
export const DebitNoteViewPage = lazy(() => import('../pages/procurement/DebitNoteViewPage'));
export const PRItemWiseReportPage = lazy(() => import('../pages/procurement/PRItemWiseReportPage'));
export const POVsPRReportPage = lazy(() => import('../pages/procurement/POVsPRReportPage'));

// Manufacturing
export const MrpPage = lazy(() => import('../pages/erp/mrp/MrpPage'));
export const WorkOrderListPage = lazy(() => import('../pages/manufacturing/WorkOrderListPage'));
export const WorkOrderFormPage = lazy(() => import('../pages/manufacturing/WorkOrderFormPage'));
export const WorkOrderViewPage = lazy(() => import('../pages/manufacturing/WorkOrderViewPage'));
export const PlanningPage = lazy(() => import('../pages/manufacturing/PlanningPage'));
export const MaterialIssuesPage = lazy(() => import('../pages/manufacturing/MaterialIssuesPage'));
export const ProductionConsumptionPage = lazy(
  () => import('../pages/manufacturing/ProductionConsumptionPage'),
);
export const GoodsReceiptVerificationPage = lazy(
  () => import('../pages/manufacturing/GoodsReceiptVerificationPage'),
);

// Sales
export const SalesOrderListPage = lazy(() => import('../pages/sales/SalesOrderListPage'));
export const SalesOrderFormPage = lazy(() => import('../pages/sales/SalesOrderFormPage'));
export const SalesOrderViewPage = lazy(() => import('../pages/sales/SalesOrderViewPage'));
export const DeliveryNoteListPage = lazy(() => import('../pages/sales/DeliveryNoteListPage'));
export const DeliveryNoteFormPage = lazy(() => import('../pages/sales/DeliveryNoteFormPage'));
export const DeliveryNoteViewPage = lazy(() => import('../pages/sales/DeliveryNoteViewPage'));
export const DispatchLedgerPage = lazy(() => import('../pages/sales/DispatchLedgerPage'));
export const SalesInvoiceListPage = lazy(() => import('../pages/sales/SalesInvoiceListPage'));
export const SalesInvoiceFormPage = lazy(() => import('../pages/sales/SalesInvoiceFormPage'));
export const SalesInvoiceViewPage = lazy(() => import('../pages/sales/SalesInvoiceViewPage'));

// Admin
export const UsersPage = lazy(() => import('../pages/admin/UsersPage'));
export const UserManagementPage = lazy(() => import('../pages/admin/UserManagementPage'));
export const RoleManagementPage = lazy(() => import('../pages/admin/RoleManagementPage'));
export const WarehouseAccessPage = lazy(() => import('../pages/admin/WarehouseAccessPage'));
export const ApiKeysPage = lazy(() => import('../pages/admin/ApiKeysPage'));
export const AdminOrganizationPage = lazy(() => import('../pages/admin/AdminOrganizationPage'));
export const ApprovalWorkflowPage = lazy(() => import('../pages/admin/ApprovalWorkflowPage'));
export const BulkDataHubPage = lazy(() => import('../pages/tools/BulkDataHubPage'));

// Profile & Organizations
export const ProfilePage = lazy(() => import('../pages/ProfilePage'));
export const OrganizationsPage = lazy(() => import('../pages/organizations/OrganizationsPage'));

// ============================================================================
// PATH TO COMPONENT MAPPING
// ============================================================================

/**
 * Maps URL paths to component names
 * This is the ONLY place you need to add new routes!
 *
 * When adding a new route:
 * 1. Add to backend menu.service.ts (controls navigation)
 * 2. Add one line here: path → component
 *
 * That's it! No ROUTE_TREE duplication needed.
 */
export const PATH_TO_COMPONENT_MAP: Record<string, string> = {
  // Dashboard
  '/dashboard': 'Dashboard',
  '/waiting-on-me': 'WaitingOnMePage',

  // Master Data
  '/master/items': 'ItemPage',
  '/master/item-groups': 'ItemGroupPage',
  '/master/warehouses': 'WarehousePage',
  '/master/uom': 'UomPage',
  '/master/uom-categories': 'UomCategoryPage',
  '/master/uom-conversions': 'UomConversionPage',
  '/master/brands': 'BrandsPage',
  '/master/parties': 'PartyPage',
  '/master/transporters': 'TransporterPage',
  '/master/payment-terms': 'PaymentTermsPage',
  '/sales/parties': 'PartyPage',

  // Inventory & Products
  '/inventory/items': 'InventoryPage',
  '/inventory/bom': 'BOMPage',
  '/inventory/stock-balances': 'StockBalancesPage',
  '/inventory/stock-ledger': 'StockLedgerPage',
  '/inventory/stock-transfer': 'StockTransferPage',
  '/inventory/stock-consumption': 'StockConsumptionPage',
  '/inventory/reports': 'InventoryReportsPage',
  '/sales/orders': 'SalesOrderListPage',
  '/sales/delivery-notes': 'DeliveryNoteListPage',
  '/sales/delivery-notes/create': 'DeliveryNoteFormPage',
  '/sales/delivery-notes/dispatch-ledger': 'DispatchLedgerPage',
  '/sales/invoices': 'SalesInvoiceListPage',
  '/sales/products': 'ProductsPage',

  // Procurement
  '/procurement/dashboard': 'ProcurementDashboard',
  '/procurement/purchase-orders': 'PurchaseOrderListPage',
  '/procurement/purchase-receipts': 'PurchaseReceiptListPage',
  '/procurement/vouchers': 'ProcurementVouchersListPage',
  '/procurement/credit-notes': 'CreditNoteListPage',
  '/procurement/debit-notes': 'DebitNoteListPage',
  '/procurement/reports/pr-item-wise': 'PRItemWiseReportPage',
  '/procurement/reports/po-vs-pr': 'POVsPRReportPage',

  // Manufacturing
  '/manufacturing/planning': 'PlanningPage',
  '/manufacturing/material-issues': 'MaterialIssuesPage',
  '/manufacturing/consumption': 'ProductionConsumptionPage',
  '/manufacturing/goods-receipt': 'GoodsReceiptVerificationPage',
  '/manufacturing/work-orders': 'WorkOrderListPage',
  '/manufacturing/mrp': 'MrpPage',

  // Admin
  '/admin/users': 'UserManagementPage',
  '/admin/roles': 'RoleManagementPage',
  '/admin/warehouse-access': 'WarehouseAccessPage',
  '/admin/organization': 'AdminOrganizationPage',
  '/admin/approval-workflows': 'ApprovalWorkflowPage',

  // Tools
  '/tools/bulk-data': 'BulkDataHubPage',
};

// ============================================================================
// COMPONENT MAP
// ============================================================================

/**
 * Maps component names to actual React components
 * Used by the router to render the correct component
 */
export const COMPONENT_MAP: Record<string, React.LazyExoticComponent<React.ComponentType<any>>> = {
  // Dashboard
  Dashboard,
  WaitingOnMePage,

  // Master Data
  ItemPage,
  ItemDetailPage,
  ItemGroupPage,
  WarehousePage,
  UomPage,
  UomCategoryPage,
  UomConversionPage,
  BrandsPage,
  PartyPage,
  TransporterPage,
  PaymentTermsPage,

  // Products & Inventory
  ProductsPage,
  OrdersPage,
  InventoryPage,
  BOMPage,
  StockBalancesPage,
  StockLedgerPage,
  StockTransferPage,
  StockTransferViewPage,
  StockConsumptionPage,
  InventoryReportsPage,

  // Procurement
  ProcurementDashboard,
  PurchaseOrderListPage,
  PurchaseReceiptListPage,
  PurchaseReceiptFormPage,
  PurchaseReceiptViewPage,
  ProcurementVouchersListPage,
  CreditNoteListPage,
  CreditNoteFormPage,
  CreditNoteViewPage,
  DebitNoteListPage,
  DebitNoteFormPage,
  DebitNoteViewPage,
  PRItemWiseReportPage,
  POVsPRReportPage,

  // Manufacturing
  MrpPage,
  PlanningPage,
  MaterialIssuesPage,
  ProductionConsumptionPage,
  GoodsReceiptVerificationPage,
  WorkOrderListPage,
  WorkOrderFormPage,
  WorkOrderViewPage,

  // Sales
  SalesOrderListPage,
  SalesOrderFormPage,
  SalesOrderViewPage,
  DeliveryNoteListPage,
  DeliveryNoteFormPage,
  DeliveryNoteViewPage,
  DispatchLedgerPage,
  SalesInvoiceListPage,
  SalesInvoiceFormPage,
  SalesInvoiceViewPage,

  // Admin
  UsersPage,
  UserManagementPage,
  RoleManagementPage,
  WarehouseAccessPage,
  AdminOrganizationPage,
  ApprovalWorkflowPage,
  BulkDataHubPage,

  // Profile & Organizations
  ProfilePage,
  OrganizationsPage,
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get component name from URL path
 * Uses the PATH_TO_COMPONENT_MAP for simple lookup
 */
export function getComponentNameFromPath(path: string): string | null {
  return PATH_TO_COMPONENT_MAP[path] || null;
}

/**
 * Get component from URL path
 * Returns the actual React component for the given path
 */
export function getComponentFromPath(
  path: string,
): React.LazyExoticComponent<React.ComponentType<any>> | null {
  const componentName = getComponentNameFromPath(path);
  return componentName ? COMPONENT_MAP[componentName] : null;
}

// ============================================================================
// STATIC ROUTES (Not in backend menu)
// ============================================================================

/**
 * Routes that don't appear in the backend menu
 * These are utility routes like profile, settings, etc.
 */
export const STATIC_ROUTES = [
  { path: '/profile', component: ProfilePage, requiresAuth: true },
  { path: '/organizations', component: OrganizationsPage, requiresAuth: true },
  { path: '/tools/bulk-data', component: BulkDataHubPage, requiresAuth: true },

  // Admin - API Keys (reachable by URL; not in the backend menu sidebar)
  { path: '/admin/api-keys', component: ApiKeysPage, requiresAuth: true },

  // Procurement - Detail pages not in menu
  {
    path: '/procurement/purchase-orders/create',
    component: PurchaseOrderFormPage,
    requiresAuth: true,
  },
  {
    path: '/procurement/purchase-orders/:id',
    component: PurchaseOrderViewPage,
    requiresAuth: true,
  },
  {
    path: '/procurement/purchase-orders/:id/edit',
    component: PurchaseOrderFormPage,
    requiresAuth: true,
  },
  {
    path: '/procurement/purchase-receipts/create',
    component: PurchaseReceiptFormPage,
    requiresAuth: true,
  },
  {
    path: '/procurement/purchase-receipts/:id',
    component: PurchaseReceiptViewPage,
    requiresAuth: true,
  },
  {
    path: '/procurement/purchase-receipts/:id/edit',
    component: PurchaseReceiptFormPage,
    requiresAuth: true,
  },
  {
    path: '/procurement/vouchers',
    component: ProcurementVouchersListPage,
    requiresAuth: true,
  },
  {
    path: '/procurement/credit-notes/create',
    component: CreditNoteFormPage,
    requiresAuth: true,
  },
  {
    path: '/procurement/credit-notes/:id',
    component: CreditNoteViewPage,
    requiresAuth: true,
  },
  {
    path: '/procurement/debit-notes/create',
    component: DebitNoteFormPage,
    requiresAuth: true,
  },
  {
    path: '/procurement/debit-notes/:id',
    component: DebitNoteViewPage,
    requiresAuth: true,
  },

  // Master - Item detail (not in menu)
  { path: '/master/items/:id', component: ItemDetailPage, requiresAuth: true },

  // Inventory
  { path: '/inventory/stock-transfer/:id', component: StockTransferViewPage, requiresAuth: true },

  // Manufacturing
  { path: '/manufacturing/work-orders/create', component: WorkOrderFormPage, requiresAuth: true },
  { path: '/manufacturing/work-orders/:id/edit', component: WorkOrderFormPage, requiresAuth: true },
  { path: '/manufacturing/work-orders/:id', component: WorkOrderViewPage, requiresAuth: true },

  // Sales
  { path: '/sales/orders/create', component: SalesOrderFormPage, requiresAuth: true },
  { path: '/sales/orders/:id', component: SalesOrderViewPage, requiresAuth: true },
  { path: '/sales/orders/:id/edit', component: SalesOrderFormPage, requiresAuth: true },
  { path: '/sales/delivery-notes', component: DeliveryNoteListPage, requiresAuth: true },
  { path: '/sales/delivery-notes/create', component: DeliveryNoteFormPage, requiresAuth: true },
  { path: '/sales/delivery-notes/:id/edit', component: DeliveryNoteFormPage, requiresAuth: true },
  {
    path: '/sales/delivery-notes/dispatch-ledger',
    component: DispatchLedgerPage,
    requiresAuth: true,
  },
  { path: '/sales/delivery-notes/:id', component: DeliveryNoteViewPage, requiresAuth: true },
  { path: '/sales/invoices', component: SalesInvoiceListPage, requiresAuth: true },
  { path: '/sales/invoices/create', component: SalesInvoiceFormPage, requiresAuth: true },
  { path: '/sales/invoices/:id', component: SalesInvoiceViewPage, requiresAuth: true },
];

// ============================================================================
// ROUTE PATHS (for convenience)
// ============================================================================

/**
 * Route path constants for type-safe navigation
 * These match the paths in PATH_TO_COMPONENT_MAP
 */
export const ROUTE_PATHS = {
  // Auth
  LOGIN: '/login',
  ACCEPT_INVITATION: '/accept-invitation',

  // Onboarding
  ONBOARDING: '/onboarding',
  ONBOARDING_ORGANIZATION: '/onboarding/organization',

  // Dashboard
  DASHBOARD: '/dashboard',
  WAITING_ON_ME: '/waiting-on-me',

  // Master Data
  MASTER_ITEMS: '/master/items',
  MASTER_ITEMS_VIEW: (itemId: string) => `/master/items/${itemId}`,
  MASTER_ITEM_GROUPS: '/master/item-groups',
  MASTER_WAREHOUSES: '/master/warehouses',
  MASTER_UOM: '/master/uom',
  MASTER_UOM_CATEGORIES: '/master/uom-categories',
  MASTER_UOM_CONVERSIONS: '/master/uom-conversions',
  MASTER_PARTIES: '/master/parties',
  MASTER_TRANSPORTERS: '/master/transporters',

  // Inventory
  INVENTORY_ITEMS: '/inventory/items',
  INVENTORY_BOM: '/inventory/bom',
  INVENTORY_STOCK_BALANCES: '/inventory/stock-balances',
  INVENTORY_STOCK_LEDGER: '/inventory/stock-ledger',
  INVENTORY_STOCK_TRANSFER: '/inventory/stock-transfer',
  INVENTORY_STOCK_TRANSFER_VIEW: (id: string) => `/inventory/stock-transfer/${id}`,
  INVENTORY_STOCK_CONSUMPTION: '/inventory/stock-consumption',
  INVENTORY_REPORTS: '/inventory/reports',

  // Sales
  SALES_ORDERS: '/sales/orders',
  SALES_ORDERS_CREATE: '/sales/orders/create',
  SALES_ORDERS_VIEW: (id: string) => `/sales/orders/${id}`,
  SALES_ORDERS_EDIT: (id: string) => `/sales/orders/${id}/edit`,
  SALES_DELIVERY_NOTES_EDIT: (id: string) => `/sales/delivery-notes/${id}/edit`,
  SALES_PRODUCTS: '/sales/products',

  // Manufacturing
  MANUFACTURING_WORK_ORDERS: '/manufacturing/work-orders',
  MANUFACTURING_WORK_ORDERS_CREATE: '/manufacturing/work-orders/create',
  MANUFACTURING_WORK_ORDERS_EDIT: (id: string) => `/manufacturing/work-orders/${id}/edit`,
  MANUFACTURING_WORK_ORDERS_VIEW: (id: string) => `/manufacturing/work-orders/${id}`,
  MANUFACTURING_JOB_CARDS: '/manufacturing/job-cards',
  MANUFACTURING_JOB_CARDS_VIEW: (id: string) => `/manufacturing/job-cards/${id}`,
  MANUFACTURING_MATERIAL_REQUISITIONS: '/manufacturing/material-requisitions',
  MANUFACTURING_MATERIAL_REQUISITIONS_VIEW: (id: string) =>
    `/manufacturing/material-requisitions/${id}`,
  MANUFACTURING_CONSUMPTION: '/manufacturing/consumption',
  MANUFACTURING_GOODS_RECEIPT: '/manufacturing/goods-receipt',
  MANUFACTURING_MRP: '/manufacturing/mrp',

  // Procurement
  PROCUREMENT_DASHBOARD: '/procurement/dashboard',
  PROCUREMENT_PURCHASE_ORDERS: '/procurement/purchase-orders',
  PROCUREMENT_PURCHASE_ORDERS_CREATE: '/procurement/purchase-orders/create',
  PROCUREMENT_PURCHASE_ORDERS_VIEW: (id: string) => `/procurement/purchase-orders/${id}`,
  PROCUREMENT_PURCHASE_ORDERS_EDIT: (id: string) => `/procurement/purchase-orders/${id}/edit`,
  PROCUREMENT_PURCHASE_RECEIPTS_VIEW: (id: string) => `/procurement/purchase-receipts/${id}`,
  PROCUREMENT_PURCHASE_RECEIPTS_EDIT: (id: string) => `/procurement/purchase-receipts/${id}/edit`,

  // Admin
  ADMIN_USERS: '/admin/users',
  ADMIN_ROLES: '/admin/roles',
  ADMIN_WAREHOUSE_ACCESS: '/admin/warehouse-access',
  ADMIN_ORGANIZATION: '/admin/organization',
  ADMIN_APPROVAL_WORKFLOWS: '/admin/approval-workflows',

  // Profile
  PROFILE: '/profile',
  ORGANIZATIONS: '/organizations',

  // Tools
  TOOLS_BULK_DATA: '/tools/bulk-data',

  // Error
  NOT_FOUND: '/404',
  UNAUTHORIZED: '/401',
  SERVER_ERROR: '/500',
} as const;
